import React, { useEffect, useRef, useState } from 'react';
import siteAnalyticsService from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, LoadError, fa } from './useDateRange';
import './analytics.css';

const SiteAnalyticsEngagement: React.FC = () => {
  const dr = useDateRange('30d');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const load = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const r = await siteAnalyticsService.getEngagementInsights(dr.range);
      if (reqId !== reqIdRef.current) return;
      setData(r);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>تعامل با نوتیفیکیشن و استوری</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={load} />}

      {loading && !data && <div className="analytics-loading">در حال بارگذاری…</div>}

      {data && (
        <>
          {/* راهنمای صفر بودن — بدون این، ادمین نمی‌فهمید صفرها یعنی «تعامل صفر» یا «رویداد ثبت نمی‌شود» */}
          {!(data.push?.received || data.push?.clicked || data.story?.views || data.banner?.views) && (
            <div className="analytics-empty">
              در این بازه هیچ رویداد تعاملی (پوش/استوری/بنر) ثبت نشده — اگر سایت استوری/بنر فعال دارد، یعنی
              نسخهٔ وبِ در حال سرویس هنوز این رویدادها را ارسال نمی‌کند (نیاز به دیپلوی figano-web).
            </div>
          )}

          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>🔔 Push Notification</h3>
            </div>
            <div className="analytics-kpi-grid" style={{ marginBottom: 0 }}>
              <div className="analytics-kpi-card">
                <div className="accent" />
                <div className="label">دریافت شده</div>
                <div className="value">{fa(data.push?.received || 0)}</div>
              </div>
              <div className="analytics-kpi-card green">
                <div className="accent" />
                <div className="label">کلیک شده</div>
                <div className="value">{fa(data.push?.clicked || 0)}</div>
              </div>
              <div className="analytics-kpi-card orange">
                <div className="accent" />
                <div className="label">CTR</div>
                <div className="value">{fa(data.push?.ctr || 0)}٪</div>
              </div>
            </div>
          </div>

          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>📖 Story Engagement</h3>
            </div>
            <div className="analytics-kpi-grid" style={{ marginBottom: 0 }}>
              <div className="analytics-kpi-card">
                <div className="accent" />
                <div className="label">بازدید استوری</div>
                <div className="value">{fa(data.story?.views || 0)}</div>
              </div>
              <div className="analytics-kpi-card green">
                <div className="accent" />
                <div className="label">تکمیل شده</div>
                <div className="value">{fa(data.story?.completed || 0)}</div>
              </div>
              <div className="analytics-kpi-card purple">
                <div className="accent" />
                <div className="label">نرخ تکمیل</div>
                <div className="value">{fa(data.story?.completionRate || 0)}٪</div>
              </div>
            </div>
          </div>

          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>🎯 Banner Engagement</h3>
            </div>
            <div className="analytics-kpi-grid" style={{ marginBottom: 0 }}>
              <div className="analytics-kpi-card">
                <div className="accent" />
                <div className="label">نمایش</div>
                <div className="value">{fa(data.banner?.views || 0)}</div>
              </div>
              <div className="analytics-kpi-card green">
                <div className="accent" />
                <div className="label">کلیک</div>
                <div className="value">{fa(data.banner?.clicked || 0)}</div>
              </div>
              <div className="analytics-kpi-card orange">
                <div className="accent" />
                <div className="label">CTR</div>
                <div className="value">{fa(data.banner?.ctr || 0)}٪</div>
              </div>
            </div>
          </div>

          {/* 🆕 تفکیک per-بنر / per-استوری — کدام «کریتیو» واقعاً جواب می‌دهد */}
          {((data.byBanner && data.byBanner.length > 0) || (data.byStory && data.byStory.length > 0)) && (
            <div className="analytics-grid-2col">
              <div className="analytics-section">
                <div className="analytics-section-header">
                  <h3>عملکرد هر بنر</h3>
                </div>
                {!data.byBanner || data.byBanner.length === 0 ? (
                  <div className="analytics-empty">داده‌ای نیست</div>
                ) : (
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>بنر</th>
                        <th>نمایش</th>
                        <th>کلیک</th>
                        <th>CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byBanner.map((b: any) => (
                        <tr key={b.bannerId}>
                          <td style={{ direction: 'ltr', fontFamily: 'monospace', fontSize: 11 }} title={b.bannerId}>
                            …{String(b.bannerId).slice(-8)}
                          </td>
                          <td>{fa(b.views)}</td>
                          <td>{fa(b.clicks)}</td>
                          <td>
                            <strong style={{ color: b.ctr >= 5 ? 'var(--color-success-dark)' : undefined }}>{fa(b.ctr)}٪</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="analytics-section">
                <div className="analytics-section-header">
                  <h3>عملکرد هر استوری</h3>
                </div>
                {!data.byStory || data.byStory.length === 0 ? (
                  <div className="analytics-empty">داده‌ای نیست</div>
                ) : (
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>استوری</th>
                        <th>بازدید</th>
                        <th>تکمیل</th>
                        <th>نرخ تکمیل</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byStory.map((s: any) => (
                        <tr key={s.storyId}>
                          <td style={{ direction: 'ltr', fontFamily: 'monospace', fontSize: 11 }} title={s.storyId}>
                            …{String(s.storyId).slice(-8)}
                          </td>
                          <td>{fa(s.views)}</td>
                          <td>{fa(s.completed)}</td>
                          <td>
                            <strong style={{ color: s.completionRate >= 60 ? 'var(--color-success-dark)' : undefined }}>
                              {fa(s.completionRate)}٪
                            </strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SiteAnalyticsEngagement;
